import type {TreeSelectPassThroughOptions} from 'primevue';
import type {TreePassThroughMethodOptions} from 'primevue/tree';

export default {
    root: ({props, state, parent, attrs}) => ({
        class: [
            // Display and Position
            'inline-flex',
            'relative',
            'h-[38px] w-full',

            // Shape
            {'rounded-md': parent.instance?.$name !== 'InputGroup'},
            {
                'first:rounded-l-md rounded-none last:rounded-r-md':
                    parent.instance?.$name == 'InputGroup',
            },
            {'border-0 border-y border-l last:border-r': parent.instance?.$name == 'InputGroup'},
            {'first:ml-0 -ml-px': parent.instance?.$name == 'InputGroup'},

            // Color and Background
            {'bg-white': !props.disabled},
            'border',
            {'border-zinc-300': !props.invalid && !attrs['highlighted']},
            {'border-d-accent': attrs['highlighted']},

            // Invalid State
            'invalid:focus:ring-red-200',
            'invalid:hover:border-red-500',
            {'border-red-500': props.invalid},

            // Transitions
            'transition-all',
            'duration-200',

            // States
            {'hover:border-primary-hover': !props.invalid},
            {
                'outline-none outline-offset-0 ring-1 ring-primary z-10': state.focused,
            },

            // Misc
            'cursor-pointer',
            'select-none',
            {
                'bg-zinc-50 select-none pointer-events-none cursor-default': props.disabled,
            },
        ],
    }),
    labelContainer: {
        class: ['overflow-hidden flex flex-auto cursor-pointer'],
    },
    label: ({props}) => ({
        class: [
            // Font
            'leading-[normal]',

            // Display
            'block',
            'flex-auto',

            // Color and Background
            'bg-transparent',
            'border-0',
            {
                'text-zinc-700': props.modelValue != null,
                'text-zinc-500': props.modelValue == null,
            },
            'placeholder:text-zinc-500',

            // Sizing and Spacing
            'w-[1%]',
            'py-2 px-3',
            {'pr-7': props.showClear},

            // Shape
            'rounded-none',

            // Transitions
            'transition',
            'duration-200',

            // States
            'focus:outline-none focus:shadow-none',

            // Misc
            'relative',
            'cursor-pointer',
            'overflow-hidden text-ellipsis',
            'whitespace-nowrap',
            'appearance-none',
        ],
    }),
    dropdown: {
        class: [
            'flex items-center justify-center',
            'shrink-0',
            'bg-transparent',
            'text-zinc-500',
            'w-12',
            'rounded-r-md',
        ],
    },
    dropdownIcon: {
        class: ['text-zinc-500'],
    },
    panel: {
        class: ['bg-white', 'text-zinc-500', 'border border-zinc-300', 'rounded-md', 'shadow-md'],
    },
    treeContainer: {
        class: ['max-h-[200px]', 'overflow-auto'],
    },
    pcTree: {
        root: {
            class: 'p-1',
        },
        pcFilterContainer: {
            root: {
                class: ['relative'],
            },
        },
        pcFilterInput: {
            root: {
                class: [
                    'h-[38px]',
                    'leading-none',
                    'w-full',
                    'py-2 px-3 pr-10',
                    'rounded-md',
                    'text-zinc-700',
                    'placeholder:text-zinc-500',
                    'bg-white',
                    'border border-zinc-300',
                    'hover:border-zinc-400',
                    'focus:outline-none focus:ring-1 focus:ring-primary focus:z-10',
                    'transition-colors duration-200',
                ],
            },
        },
        pcFilterIconContainer: {
            root: {
                class: ['absolute', 'top-1/2', '-mt-2', 'right-3', 'text-zinc-500'],
            },
        },
        filterIcon: {
            class: ['text-zinc-500', 'w-4', 'h-4'],
        },
        nodeContent: ({context}: TreePassThroughMethodOptions) => {
            // Determine hierarchy level based on node type
            // Location (level 0): pl-3
            // Floor (level 1): pl-6
            // Room (level 2): pl-9
            const level =
                context?.node?.data?.type === 'location'
                    ? 0
                    : context?.node?.data?.type === 'floor'
                      ? 1
                      : context?.node?.data?.type === 'room'
                        ? 2
                        : 0;

            return {
                class: [
                    'relative',
                    'flex items-center',
                    // Font
                    'leading-none',
                    // Spacing
                    'm-0 py-2',
                    'first:mt-0 mt-[2px]',
                    // Progressive padding based on hierarchy level
                    level === 0 ? 'pl-3 pr-3' : '',
                    level === 1 ? 'pl-6 pr-3' : '',
                    level === 2 ? 'pl-9 pr-3' : '',
                    // Shape
                    'border-0 rounded',
                    // Colors
                    {
                        'text-zinc-700': context.selected,
                        'text-zinc-500': !context.selected,
                        'bg-primary-highlight': context.selected,
                    },
                    // States
                    {'hover:bg-zinc-100': true},
                    // Transition
                    'transition-shadow duration-200',
                    // Misc
                    'cursor-pointer overflow-hidden whitespace-nowrap',
                ],
            };
        },
        nodeToggleButton: ({context}: TreePassThroughMethodOptions) => ({
            class: [
                'mr-2',
                {
                    invisible: context.leaf,
                    visible: !context.leaf,
                },
                'cursor-pointer',
                'inline-flex items-center justify-center',
                'w-4 h-4',
                'text-zinc-500',
                'hover:text-zinc-700',
                'transition-colors duration-200',
            ],
        }),
        nodeToggleIcon: {
            class: ['text-zinc-500', 'w-4', 'h-4'],
        },
        nodeLabel: {
            class: ['flex-1', 'text-sm'],
        },
        nodeIcon: {
            class: ['mr-2', 'text-zinc-500'],
        },
    },
    emptyMessage: {
        class: ['leading-none', 'py-2 px-3', 'text-zinc-700', 'bg-transparent'],
    },
    clearIcon: {
        class: ['text-zinc-500', 'absolute', 'top-1/2', 'right-12', '-mt-2'],
    },
    transition: {
        enterFromClass: 'opacity-0 scale-y-[0.8]',
        enterActiveClass: 'transition-[transform,opacity] duration-120 ease-out',
        leaveActiveClass: 'transition-opacity duration-100 ease-linear',
        leaveToClass: 'opacity-0',
    },
} satisfies TreeSelectPassThroughOptions;
